/**
 * Una cotización cargada en la caja, con los precios de HOY.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL FALLO QUE ESTO ARREGLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Al pasar una cotización a venta, el servidor cobra con el catálogo de hoy —
 * es la decisión de siempre: una cotización no congela precios. Pero la cuenta
 * se armaba con los precios del PAPEL de la cotización. Si algo había subido
 * desde entonces, el cajero pedía un monto y la venta se registraba con otro.
 * Lo encontró la auditoría.
 *
 * Ahora cada línea toma los datos de hoy del catálogo guardado en el aparato —
 * los mismos con los que se arma cualquier otra venta— y se cuenta cuántos
 * precios cambiaron, para decírselo al cajero antes de cobrar.
 */

import type { Cotizacion, ItemListRow } from "@/lib/types";
import { precioDeLinea, type CotizacionEnLaCaja, type ExtraEnCarrito } from "@/stores/carrito";

export function aLaCajaConPreciosDeHoy(
  cot: Cotizacion,
  deHoy: ReadonlyMap<string, ItemListRow>,
): { enLaCaja: CotizacionEnLaCaja; cambiaron: number; sinCatalogo: number } {
  let cambiaron = 0;
  let sinCatalogo = 0;

  const lineas: CotizacionEnLaCaja["lineas"] = cot.items.map((l) => {
    const cantidad = Number(l.qty) || 0;
    const descuento = Number(l.discount) || 0;
    const item = l.item_id ? deHoy.get(l.item_id) : undefined;
    /*
      Los extras que pidió el cliente («+ Queso»). Viajan con la línea y el
      servidor les pone el precio al cobrar. Uno que se borró del catálogo ya
      no se puede cobrar: se queda fuera, y el precio que cambia se cuenta.
    */
    const opciones: ExtraEnCarrito[] = (l.opciones ?? [])
      .filter((o): o is typeof o & { id: string } => o.id != null)
      .map((o) => ({ id: o.id, nombre: o.nombre, precio: o.precio }));

    /*
      El delivery de un pedido de la página (2026-11-02): servicio de monto
      variable que no está en la pantalla de venta. Va con su monto, que es lo
      que cobra el servidor, y no cuenta como «sin catálogo» ni como precio
      cambiado: su precio es el que escribió quien atendió.
    */
    if (l.monto != null && l.item_id) {
      return {
        itemId: l.item_id,
        variantId: l.variant_id,
        nombre: l.name,
        varianteNombre: l.variant_name,
        cantidad,
        precioBase: l.unit_price,
        descuento,
        kind: l.kind,
        tasaImpuesto: l.tax_rate,
        impuestoNombre: l.tax_name,
        monto: Number(l.monto),
      };
    }

    if (!item) {
      // Sin el producto en el aparato: se queda lo que dice el papel. Su
      // precio ya lleva los extras sumados; se apartan para que viajen como
      // extras (el servidor los cobra) y no se sumen dos veces.
      if (l.item_id) sinCatalogo++;
      const extras = opciones.reduce((s, o) => s + (Number(o.precio) || 0), 0);
      return {
        itemId: l.item_id,
        variantId: l.variant_id,
        nombre: l.name,
        varianteNombre: l.variant_name,
        cantidad,
        precioBase: extras ? String((Number(l.unit_price) || 0) - extras) : l.unit_price,
        descuento,
        kind: l.kind,
        tasaImpuesto: l.tax_rate,
        impuestoNombre: l.tax_name,
        opciones,
      };
    }

    /*
      ⚠️ LA VARIACIÓN SUMA SU DIFERENCIA (2026-10-15). La hamburguesa de carne
      vale 6 aunque el plato diga 5: el servidor cobra el precio del plato más
      la diferencia de la variación, en la lista que sea. Antes la caja cargaba
      el precio del plato a secas y el cobro no cuadraba con el del servidor.
    */
    const variacion = l.variant_id ? item.variaciones?.find((v) => v.id === l.variant_id) : undefined;
    const diferencia = variacion ? Number(variacion.precio) - Number(item.base_price) : 0;
    const conDiferencia = (p: string) => (diferencia ? String(Number(p) + diferencia) : p);

    const linea = {
      itemId: item.id,
      variantId: l.variant_id,
      nombre: item.name,
      varianteNombre: l.variant_name,
      cantidad,
      precioBase: conDiferencia(item.base_price),
      descuento,
      kind: item.kind,
      tasaImpuesto: item.tax_rate ?? "0",
      impuestoNombre: item.tax_name ?? null,
      // Lo exento de HOY: el servidor vuelve a calcular con el catálogo de hoy.
      exentoDe: item.impuestos_exentos ?? [],
      preciosPorLista: Object.fromEntries(
        Object.entries(item.precios ?? {}).map(([lista, p]) => [lista, p == null ? p : conDiferencia(String(p))]),
      ) as Record<string, string>,
      impuestoIncluido: item.tax_inclusive ?? true,
      unidad: item.unit ?? null,
      requierePersonal: Boolean(item.requires_staff),
      opciones,
    };

    // ¿Cambió el precio que se va a cobrar? Con la misma lista que la cotización.
    const hoy = precioDeLinea(linea, cot.lista_precios_id);
    if (Math.abs(hoy - (Number(l.unit_price) || 0)) > 0.00005) cambiaron++;

    return linea;
  });

  return {
    enLaCaja: {
      id: cot.id,
      numero: cot.numero,
      clienteId: cot.customer_id,
      clienteNombre: cot.customer_name,
      listaPreciosId: cot.lista_precios_id,
      impuestos: cot.impuestos.map((t) => t.tax_id).filter((x): x is string => !!x),
      lineas,
    },
    cambiaron,
    sinCatalogo,
  };
}
